import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classNames from 'classnames';

import messages from './Navigation.messages';
import { NavLink } from './NavLink';
import styles from './DropdownMenu.module.css';
import { isIwocaPaySeller } from '../../api/iwocaPaySelector';
import { GoogleTagManager } from '@iwoca/frontend-tracking-library';
import ProductDashboardLinks from './ProductDashboardLinks';
import IwMedia from '../../iwMedia/IwMedia';
import { OPEN_MODAL } from '../../iwModals/Modals.actions';
import { RootState } from '../../iwProvider/configureStore';
import { Dispatch } from 'redux';
import { getIwocaPayDashboardLink } from './Navigation';
import { getCountry } from '@iwIntlProvider/CountryProvider';

const mapStateToProps = (state: RootState) => ({
    isIwocaPaySeller: isIwocaPaySeller(state),
    ...state.navigation
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    openHelpModal: () =>
        dispatch({
            type: OPEN_MODAL,
            id: 'HelpModal'
        })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

const DropdownMenu = ({
    isOpen,
    setIsOpen,
    isIwocaPaySeller,
    openHelpModal
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
} & PropsFromRedux) => {
    return (
        <div
            data-testid="dropdown-navigation"
            className={classNames(
                styles.DropdownNavigation,
                isOpen ? styles.navigationOpen : styles.navigationClosed
            )}
        >
            <div className={styles.navigationContainer}>
                <IwMedia to="small">
                    <div className={styles.navItemSection}>
                        {isIwocaPaySeller && (
                            <a
                                data-testid="iwocapay-link"
                                className={styles.dropdownNavLink}
                                href={getIwocaPayDashboardLink()}
                            >
                                <FormattedMessage
                                    {...messages.iwocapaySeller}
                                />
                            </a>
                        )}
                        <button
                            className={classNames(
                                styles.dropdownNavLink,
                                styles.dropdownNavButton
                            )}
                            onClick={() => {
                                setIsOpen(false);
                                openHelpModal();
                            }}
                        >
                            <FormattedMessage {...messages.help} />
                        </button>
                    </div>
                </IwMedia>

                <IwMedia to="medium">
                    <div className={styles.navItemSection}>
                        <span className={styles.navItemSectionHeader}>
                            <FormattedMessage {...messages.loanDashboard} />
                        </span>

                        <div data-testid="product-links-mobile">
                            <ProductDashboardLinks
                                className={styles.dropdownNavLink}
                                activeClassName={styles.dropdownNavLinkActive}
                            />
                        </div>
                    </div>
                </IwMedia>

                <div className={styles.navItemSection}>
                    <span className={styles.navItemSectionHeader}>
                        <FormattedMessage {...messages.account} />
                    </span>

                    <NavLink
                        data-testid="company-link"
                        className={styles.dropdownNavLink}
                        activeClassName={styles.dropdownNavLinkActive}
                        to="/company/"
                    >
                        <FormattedMessage {...messages.yourCompany} />
                    </NavLink>

                    <NavLink
                        data-testid="history-link"
                        className={styles.dropdownNavLink}
                        activeClassName={styles.dropdownNavLinkActive}
                        to="/history/"
                    >
                        <FormattedMessage {...messages.accountHistory} />
                    </NavLink>

                    <NavLink
                        data-testid="trading-data-link"
                        className={styles.dropdownNavLink}
                        activeClassName={styles.dropdownNavLinkActive}
                        to="/trading/"
                    >
                        <FormattedMessage {...messages.tradingData} />
                    </NavLink>

                    <NavLink
                        data-testid="contracts-data-link"
                        className={styles.dropdownNavLink}
                        activeClassName={styles.dropdownNavLinkActive}
                        to="/contracts/"
                    >
                        <FormattedMessage {...messages.contracts} />
                    </NavLink>

                    <a
                        href={
                            getCountry() === 'UK'
                                ? 'https://www.iwoca.co.uk/compliance/terms-of-use#1-general'
                                : 'https://www.iwoca.de/compliance/allgemeine-geschaeftsbedingungen'
                        }
                        className={styles.dropdownNavLink}
                        target="_blank"
                        rel="noreferrer"
                    >
                        <FormattedMessage {...messages.termsOfuse} />
                    </a>

                    <a
                        href="/my_account/logout/"
                        className={classNames(
                            styles.dropdownNavLink,
                            styles.logout
                        )}
                        onClick={() =>
                            GoogleTagManager.trackClick({
                                identifier: 'Logout',
                                deviceType: 'Desktop'
                            })
                        }
                    >
                        <FormattedMessage {...messages.logout} />
                    </a>
                </div>
            </div>
        </div>
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(DropdownMenu);
